import React, { useEffect, useMemo, useState } from "react";
import apiClient from "@/api/client";
import { ChevronLeft, ChevronRight, Calendar as CalIcon, Filter, ExternalLink, Video } from "lucide-react";
import { useNavigate } from "react-router-dom";

const TYPE_FILTERS = [
  { key: "all", label: "Tümü", color: "#0F172A" },
  { key: "meeting", label: "Toplantı", color: "#4F46E5" },
  { key: "lesson", label: "Eğitim", color: "#10B981" },
  { key: "task", label: "Görev", color: "#F59E0B" },
];

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [view, setView] = useState("month"); // "month" | "week" | "agenda"
  const [refDate, setRefDate] = useState(new Date());
  const [filter, setFilter] = useState("all");
  const [mineOnly, setMineOnly] = useState(true);
  const [selected, setSelected] = useState(null);
  const nav = useNavigate();

  const load = () => apiClient.get("/calendar/events", { params: { mine: mineOnly } }).then(r => setEvents(r.data || []));
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [mineOnly]);

  const filtered = events.filter(e => filter === "all" || e.type === filter);

  // Month
  const monthStart = useMemo(() => { const d = new Date(refDate); d.setDate(1); const wd = (d.getDay() + 6) % 7; d.setDate(d.getDate() - wd); d.setHours(0,0,0,0); return d; }, [refDate]);
  const monthDays = useMemo(() => { const out = []; const d = new Date(monthStart); for (let i=0;i<42;i++) { out.push(new Date(d)); d.setDate(d.getDate()+1); } return out; }, [monthStart]);
  const monthLabel = refDate.toLocaleDateString("tr-TR", { month: "long", year: "numeric" });

  const eventsByDay = useMemo(() => {
    const m = {};
    filtered.forEach(e => { if (!e.start) return; const k = e.start.slice(0,10); (m[k] = m[k] || []).push(e); });
    return m;
  }, [filtered]);

  // Week
  const weekStart = useMemo(() => { const d = new Date(refDate); const wd = (d.getDay() + 6) % 7; d.setDate(d.getDate() - wd); d.setHours(0,0,0,0); return d; }, [refDate]);
  const weekDays = useMemo(() => { const out = []; for (let i=0;i<7;i++) { const d = new Date(weekStart); d.setDate(d.getDate()+i); out.push(d); } return out; }, [weekStart]);

  // Agenda — next 30 events
  const agenda = useMemo(() => {
    const now = new Date();
    return filtered.filter(e => e.start && new Date(e.start) >= new Date(now.toDateString())).sort((a,b)=>a.start.localeCompare(b.start)).slice(0, 50);
  }, [filtered]);

  const shift = (n) => {
    if (view === "month") setRefDate(new Date(refDate.getFullYear(), refDate.getMonth() + n, 1));
    else if (view === "week") { const d = new Date(refDate); d.setDate(d.getDate() + n*7); setRefDate(d); }
    else { const d = new Date(refDate); d.setDate(d.getDate() + n*30); setRefDate(d); }
  };

  const eventClick = (e) => {
    setSelected(e);
  };

  const dayKey = (d) => d.toISOString().slice(0,10);
  const isToday = (d) => dayKey(d) === new Date().toISOString().slice(0,10);
  const colorOf = (e) => e.color || (e.type === "meeting" ? "#4F46E5" : e.type === "lesson" ? "#10B981" : e.type === "task" ? "#F59E0B" : "#94A3B8");

  const counts = useMemo(() => {
    const c = { meeting: 0, lesson: 0, task: 0 };
    events.forEach(e => { if (c[e.type] != null) c[e.type]++; });
    return c;
  }, [events]);

  return (
    <div className="space-y-5" data-testid="calendar-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><CalIcon size={22}/>Takvim</h1>
          <p className="text-sm text-[#64748B] mt-1">{counts.meeting} toplantı · {counts.lesson} eğitim · {counts.task} görev</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 bg-white border border-[#ECECEF] rounded-md cursor-pointer hover:bg-[#F8FAFC]" data-testid="mine-only-toggle">
            <input type="checkbox" checked={mineOnly} onChange={e => setMineOnly(e.target.checked)} className="cursor-pointer"/>
            Sadece benimkiler
          </label>
          <div className="flex items-center gap-1 bg-[#F4F4F5] rounded-md p-0.5">
            {["month","week","agenda"].map(v => (
              <button key={v} onClick={()=>setView(v)} className={`px-3 py-1 text-xs font-medium rounded transition ${view===v?"bg-white shadow-sm":"text-[#64748B]"}`} data-testid={`view-${v}`}>
                {v === "month" ? "Ay" : v === "week" ? "Hafta" : "Ajanda"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 ky-card px-3 py-2">
          <button onClick={()=>shift(-1)} className="ky-btn ky-btn-ghost ky-btn-icon"><ChevronLeft size={14}/></button>
          <button onClick={()=>setRefDate(new Date())} className="ky-btn ky-btn-outline ky-btn-sm">Bugün</button>
          <button onClick={()=>shift(1)} className="ky-btn ky-btn-ghost ky-btn-icon"><ChevronRight size={14}/></button>
          <span className="ml-2 font-semibold capitalize text-sm">{view==="agenda"?"Yaklaşan":monthLabel}</span>
        </div>
        <div className="flex items-center gap-1 ml-auto"><Filter size={12} className="text-[#94A3B8]"/>
          {TYPE_FILTERS.map(f => (
            <button key={f.key} onClick={()=>setFilter(f.key)} className={`px-2.5 py-1.5 text-xs rounded-md border ${filter===f.key?"border-[#4F46E5] bg-[#EEF2FF] text-[#4F46E5] font-medium":"border-[#ECECEF] bg-white text-[#64748B] hover:bg-[#FAFAFB]"}`} data-testid={`filter-${f.key}`}>
              {f.key !== "all" && <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5" style={{ background: f.color }}/>}
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {view === "month" && (
        <div className="ky-card overflow-hidden">
          <div className="grid grid-cols-7 gap-px bg-[#ECECEF] text-xs">
            {["Pzt","Sal","Çar","Per","Cum","Cmt","Paz"].map(d => <div key={d} className="bg-[#FAFAFB] py-2 text-center font-semibold text-[#64748B]">{d}</div>)}
            {monthDays.map((d,i) => {
              const k = dayKey(d); const list = eventsByDay[k] || [];
              const inMonth = d.getMonth() === refDate.getMonth();
              return (
                <div key={i} className={`bg-white min-h-[110px] p-2 ${!inMonth ? "opacity-50" : ""} ${isToday(d) ? "bg-[#EEF2FF]/40 ring-2 ring-[#4F46E5] ring-inset" : ""}`}>
                  <div className={`text-[11px] font-semibold mb-1.5 ${isToday(d)?"text-[#4F46E5]":"text-[#0F172A]"}`}>{d.getDate()}</div>
                  <div className="space-y-1">
                    {list.slice(0,4).map((e,j) => (
                      <button key={j} onClick={()=>eventClick(e)} className="block w-full text-left text-[10px] truncate px-1.5 py-1 rounded font-medium" style={{ background: `${colorOf(e)}1a`, color: colorOf(e) }} data-testid={`event-${e.id}`}>
                        {e.title}
                      </button>
                    ))}
                    {list.length > 4 && <div className="text-[10px] text-[#94A3B8] px-1">+{list.length-4} daha</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "week" && (
        <div className="ky-card overflow-hidden">
          <div className="grid grid-cols-7 gap-px bg-[#ECECEF] text-xs">
            {weekDays.map((d,i) => (
              <div key={i} className={`bg-[#FAFAFB] py-2 text-center ${isToday(d)?"bg-[#EEF2FF]":""}`}>
                <div className="text-[10px] uppercase font-semibold text-[#64748B]">{d.toLocaleDateString("tr-TR",{weekday:"short"})}</div>
                <div className={`text-base font-semibold mt-0.5 ${isToday(d)?"text-[#4F46E5]":""}`}>{d.getDate()}</div>
              </div>
            ))}
            {weekDays.map((d,i) => {
              const list = eventsByDay[dayKey(d)] || [];
              return (
                <div key={`b${i}`} className="bg-white min-h-[420px] p-2 space-y-1.5">
                  {list.length === 0 && <div className="text-[11px] text-[#94A3B8] text-center mt-4">—</div>}
                  {list.map((e,j) => (
                    <button key={j} onClick={()=>eventClick(e)} className="block w-full text-left text-xs px-2 py-1.5 rounded-md font-medium border-l-2" style={{ background: `${colorOf(e)}1a`, color: colorOf(e), borderColor: colorOf(e) }}>
                      <div className="truncate">{e.title}</div>
                      {e.start && <div className="text-[10px] opacity-70 mt-0.5">{new Date(e.start).toLocaleTimeString("tr-TR",{hour:"2-digit",minute:"2-digit"})}</div>}
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "agenda" && (
        <div className="ky-card overflow-hidden">
          {agenda.length === 0 && <div className="p-12 text-center text-sm text-[#94A3B8]">Yaklaşan etkinlik yok.</div>}
          {agenda.map((e,i) => {
            const d = new Date(e.start);
            const prev = i > 0 ? new Date(agenda[i-1].start) : null;
            const newDay = !prev || prev.toDateString() !== d.toDateString();
            return (
              <React.Fragment key={e.id}>
                {newDay && <div className="px-5 py-2 bg-[#FAFAFB] border-y border-[#ECECEF] text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">{d.toLocaleDateString("tr-TR",{weekday:"long",day:"numeric",month:"long"})}</div>}
                <button onClick={()=>eventClick(e)} className="w-full text-left flex items-center gap-4 px-5 py-3 hover:bg-[#FAFAFB] border-b border-[#ECECEF]">
                  <div className="text-xs font-mono text-[#64748B] w-14 shrink-0">{d.toLocaleTimeString("tr-TR",{hour:"2-digit",minute:"2-digit"})}</div>
                  <span className="w-1 h-8 rounded-full shrink-0" style={{ background: colorOf(e) }}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{e.title}</div>
                    <div className="text-[11px] text-[#94A3B8] mt-0.5">{e.type === "meeting" ? "Toplantı" : e.type === "lesson" ? "Eğitim" : e.type === "task" ? "Görev son tarihi" : e.type}</div>
                  </div>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex" onClick={()=>setSelected(null)}>
          <div className="flex-1 bg-black/40"/>
          <div onClick={e=>e.stopPropagation()} className="w-full sm:w-[440px] bg-white flex flex-col ky-slide-in" data-testid="event-detail">
            <div className="px-5 py-4 border-b border-[#ECECEF]">
              <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">{selected.type}</div>
              <h3 className="font-semibold mt-0.5">{selected.title}</h3>
              <div className="text-xs text-[#64748B] mt-1">{new Date(selected.start).toLocaleString("tr-TR")}</div>
            </div>
            <div className="p-5 space-y-3 flex-1">
              <div className="flex flex-wrap gap-2">
                {selected.type === "meeting" && <button onClick={()=>{ setSelected(null); nav("/toplantilar"); }} className="ky-btn ky-btn-outline ky-btn-sm"><ExternalLink size={11}/>Toplantı sayfası</button>}
                {selected.type === "lesson" && <button onClick={()=>{ setSelected(null); nav("/ogrenciler"); }} className="ky-btn ky-btn-outline ky-btn-sm"><ExternalLink size={11}/>Öğrenci sayfası</button>}
                {selected.type === "task" && <button onClick={()=>{ setSelected(null); nav("/gorevler"); }} className="ky-btn ky-btn-outline ky-btn-sm"><ExternalLink size={11}/>Görevler</button>}
                {selected.meeting_url && <a href={selected.meeting_url} target="_blank" rel="noreferrer" className="ky-btn ky-btn-accent ky-btn-sm"><Video size={11}/>Google Meet</a>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
